import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Check, Phone } from "lucide-react";
import SEO from "../components/SEO";
import EditorialMarquee from "../components/EditorialMarquee";
import StatusBadge from "../components/StatusBadge";
import { FadeUp, MaskedLines, SectionLabel } from "../components/Reveal";
import { SERVICE_PAGES } from "../data/services-pages";
import { PHONE_DISPLAY, PHONE_TEL } from "../data/site";

export default function ServicePage() {
    const { pathname } = useLocation();
    const data = SERVICE_PAGES.find((s) => s.slug === pathname);
    if (!data) return null;

    const priceMatch = data.price.match(/(\d+)/);
    const schema = {
        "@context": "https://schema.org",
        "@type": "Service",
        name: `${data.name} Paris`,
        serviceType: data.name,
        provider: {
            "@type": "Locksmith",
            name: "Serrurier Paris",
            telephone: "+33625258619",
            url: "https://www.serrurierparis.paris/",
            image: "https://www.serrurierparis.paris/img/logo-serrurierparis.webp",
        },
        areaServed: ["Paris", "Île-de-France"],
        ...(priceMatch && {
            offers: {
                "@type": "Offer",
                priceCurrency: "EUR",
                price: priceMatch[1],
                url: `https://www.serrurierparis.paris${data.slug}`,
            },
        }),
    };

    return (
        <div data-testid="service-page">
            <SEO title={data.metaTitle} description={data.metaDesc} path={data.slug} schema={schema} />

            {/* HERO */}
            <section className="grain relative overflow-hidden bg-midnight py-10 text-white md:py-16">
                <div className="pointer-events-none absolute -right-24 top-0 h-80 w-80 rounded-full bg-emergency/20 blur-3xl" />
                <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <nav className="mb-5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/50" data-testid="breadcrumb">
                        <Link to="/" className="transition-colors hover:text-gold">Accueil</Link>
                        <span>/</span>
                        <span>Services</span>
                        <span>/</span>
                        <span className="text-gold">{data.name}</span>
                    </nav>
                    <SectionLabel dark>Service — Paris & Île-de-France</SectionLabel>
                    <StatusBadge className="mt-4" />
                    <h1 className="mt-3 max-w-4xl font-display text-4xl font-extrabold leading-[1.02] tracking-tighter sm:text-5xl lg:text-6xl">
                        <MaskedLines delay={0.15} lines={[`${data.name} à Paris.`]} />
                        <MaskedLines delay={0.45} lines={[data.price]} lineClassName="text-[#f97316]" />
                    </h1>
                    <FadeUp delay={0.55}>
                        <p className="mt-6 max-w-xl leading-relaxed text-white/70">{data.heroIntro}</p>
                        <div className="mt-8 flex flex-wrap gap-4">
                            <a
                                href={PHONE_TEL}
                                data-testid="service-call-button"
                                className="flex items-center gap-3 bg-emergency px-7 py-4 font-bold text-white transition-colors hover:bg-emergency-dark"
                            >
                                <Phone className="h-5 w-5" /> {PHONE_DISPLAY}
                            </a>
                            <Link
                                to="/devis-contact"
                                data-testid="service-devis-button"
                                className="flex items-center gap-2 border border-white/30 px-7 py-4 font-bold text-white transition-colors hover:border-gold hover:bg-gold hover:text-midnight"
                            >
                                Devis gratuit <ArrowRight className="h-4 w-4" />
                            </Link>
                        </div>
                    </FadeUp>
                </div>
            </section>

            <EditorialMarquee dark={false} />

            {/* INCLUS */}
            <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-8">
                <div className="grid gap-12 lg:grid-cols-[1fr_1.5fr] lg:gap-20">
                    <FadeUp>
                        <SectionLabel>Prestation complète</SectionLabel>
                        <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight md:text-5xl">
                            Ce que comprend notre intervention
                        </h2>
                        <p className="mt-6 leading-relaxed text-midnight/70">
                            Chaque intervention est précédée d'un devis gratuit et sans engagement. Le prix annoncé
                            est le prix payé : aucune majoration cachée, aucune mauvaise surprise.
                        </p>
                    </FadeUp>
                    <div className="grid gap-px bg-midnight/10 sm:grid-cols-2" data-testid="service-includes-grid">
                        {data.includes.map((item, i) => (
                            <FadeUp key={item} delay={(i % 6) * 0.04} className="bg-white">
                                <div className="flex h-full items-center gap-3 px-5 py-4 text-sm font-semibold text-midnight/80">
                                    <Check className="h-4 w-4 shrink-0 text-emergency" /> {item}
                                </div>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            {/* BLOCS ÉDITORIAUX */}
            <section className="bg-white py-16 md:py-24">
                <div className="mx-auto max-w-7xl space-y-20 px-4 sm:px-6 lg:px-8">
                    {[data.block1, data.block2].map((b, i) => (
                        <div key={b.title} className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
                            <FadeUp className={i === 1 ? "lg:order-2" : ""}>
                                <img
                                    src={b.img}
                                    alt={`${data.name} Paris — Serrurier Paris`}
                                    className="aspect-[4/3] w-full rounded-xl object-cover shadow-[0_24px_60px_-20px_rgba(10,17,40,0.28)]"
                                    loading="lazy"
                                />
                            </FadeUp>
                            <div className={i === 1 ? "lg:order-1" : ""}>
                                <FadeUp>
                                    <span className="font-display text-5xl font-extrabold text-midnight/15">{String(i + 1).padStart(2, "0")}</span>
                                    <p className="mt-3 text-xs font-bold uppercase tracking-[0.25em] text-emergency">{b.kicker}</p>
                                    <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight md:text-4xl">{b.title}</h2>
                                    {b.paras.map((p, j) => (
                                        <p key={j} className="mt-5 leading-relaxed text-midnight/70">{p}</p>
                                    ))}
                                </FadeUp>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* PRIX */}
            <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-8">
                <FadeUp>
                    <div className="flex flex-wrap items-center justify-between gap-8 rounded-xl border border-midnight/10 bg-white p-8 shadow-[0_24px_60px_-20px_rgba(10,17,40,0.28)] md:p-12" data-testid="service-price-card">
                        <div>
                            <p className="text-xs font-bold uppercase tracking-[0.25em] text-midnight/40">Tarif — {data.name}</p>
                            <p className="mt-3 font-display text-5xl font-extrabold tracking-tight text-[#f97316]">{data.price}</p>
                            <p className="mt-4 max-w-lg text-sm leading-relaxed text-midnight/60">{data.note}</p>
                        </div>
                        <a
                            href={PHONE_TEL}
                            data-testid="service-price-call-button"
                            className="flex items-center gap-3 bg-emergency px-8 py-4 text-lg font-bold text-white transition-colors hover:bg-emergency-dark"
                        >
                            <Phone className="h-5 w-5" /> {PHONE_DISPLAY}
                        </a>
                    </div>
                </FadeUp>
            </section>

            {/* FAQ SERVICE */}
            {data.faq && data.faq.length > 0 && (
            <section className="bg-cloud py-16 md:py-24">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <FadeUp>
                        <SectionLabel>Questions fréquentes</SectionLabel>
                        <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight md:text-5xl">
                            FAQ — {data.name} à Paris
                        </h2>
                    </FadeUp>
                    <div className="mt-10 space-y-4">
                        {data.faq.map((item, i) => (
                            <FadeUp key={item.q} delay={i * 0.06}>
                                <details className="group border border-midnight/10 bg-white">
                                    <summary className="flex cursor-pointer items-center justify-between px-7 py-5 font-display text-lg font-bold tracking-tight">
                                        {item.q}
                                        <ArrowRight className="h-4 w-4 shrink-0 rotate-90 transition-transform group-open:rotate-[270deg]" />
                                    </summary>
                                    <div className="px-7 pb-6 pt-1 leading-relaxed text-midnight/70">{item.a}</div>
                                </details>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>
            )}

            {/* GARANTIES */}
            <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
                <FadeUp>
                    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                        {[
                            { stat: "1985", label: "Artisan depuis", desc: "Expertise transmise depuis 40 ans dans la serrurerie parisienne." },
                            { stat: "25 min", label: "Délai moyen", desc: "Intervention rapide sur Paris et petite couronne, 24h/24." },
                            { stat: "100%", label: "Devis gratuit", desc: "Estimation au téléphone, devis écrit sur site avant intervention." },
                            { stat: "A2P", label: "Marques certifiées", desc: "Fichet, Bricard, Vachette, Mul-T-Lock, DOM, JPM — certifiés NF." },
                        ].map((g, i) => (
                            <FadeUp key={g.stat} delay={i * 0.07}>
                                <div className="border border-midnight/10 bg-white p-6 text-center">
                                    <p className="font-display text-4xl font-extrabold text-[#f97316]">{g.stat}</p>
                                    <p className="mt-1 text-xs font-bold uppercase tracking-wider text-midnight/40">{g.label}</p>
                                    <p className="mt-3 text-sm leading-relaxed text-midnight/65">{g.desc}</p>
                                </div>
                            </FadeUp>
                        ))}
                    </div>
                </FadeUp>
            </section>


            {/* CTA */}
            <section className="grain bg-midnight py-16 text-white md:py-20">
                <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-8 px-4 sm:px-6 lg:px-8">
                    <div>
                        <h2 className="font-display text-3xl font-extrabold tracking-tight md:text-4xl">
                            Besoin de « {data.name} » ? Intervention en 30 minutes.
                        </h2>
                        <p className="mt-3 text-white/60">24h/24 — 7j/7, même les jours fériés. Devis gratuit au téléphone.</p>
                    </div>
                    <a
                        href={PHONE_TEL}
                        data-testid="service-bottom-call-button"
                        className="flex items-center gap-3 bg-emergency px-8 py-4 text-lg font-bold text-white transition-colors hover:bg-emergency-dark"
                    >
                        <Phone className="h-5 w-5" /> {PHONE_DISPLAY}
                    </a>
                </div>
            </section>
        </div>
    );
}
